import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid } from "@material-ui/core";
import { DateTime } from "luxon";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import ProviderAccessWrapper from "../../../app/components/access-wrappers/ProviderAccessWrapper";
import SpinnerButton from "../../../app/components/buttons/SpinnerButton";
import ControlledSelect, {
  SelectOption,
} from "../../../app/components/inputs/ControlledSelect";
import { RequestStateDto } from "../../../app/dtos/RequestStateDto";
import { monthOptions } from "../../../app/enums/MonthEnum";
import { getIsFnyfsUser } from "../../authentication/state/authenticationActions";
import {
  ProviderDto,
  providerToOptionDtos,
} from "../../provider/dtos/ProviderDto";
import { MonthlyDeliverablesReportParamsDto } from "../dtos/MonthlyDeliverablesReportParametersDto";

interface Props {
  providers: ProviderDto[];
  getProvidersRequestState: RequestStateDto;
  invoiceYears: number[];
  getInvoiceYearsRequestState: RequestStateDto;
  requestState: RequestStateDto;
  onSubmit(params: MonthlyDeliverablesReportParamsDto): Promise<void>;
}

const MonthlyDeliverableReportFilter: React.FC<Props> = ({
  providers,
  getProvidersRequestState,
  requestState,
  invoiceYears,
  getInvoiceYearsRequestState,
  onSubmit,
}) => {
  const {
    control,
    handleSubmit,
    formState: { isValid },
    watch,
  } = useForm({
    defaultValues: new MonthlyDeliverablesReportParamsDto(),
    resolver: yupResolver(MonthlyDeliverablesReportParamsDto.getSchema()),
    mode: "all",
  });
  const isProviderUser = !getIsFnyfsUser();
  const [availableMonthOptions, setAvailableMonthOptions] =
    useState<SelectOption[]>(monthOptions);

  const { year } = watch();

  useEffect(() => {
    const now = DateTime.now();
    if (now.year !== +year) {
      setAvailableMonthOptions(monthOptions);
    } else {
      setAvailableMonthOptions(
        monthOptions.filter((x) => x.value === "" || +x.value < now.month - 1)
      );
    }
  }, [year, setAvailableMonthOptions]);

  return (
    <form
      onSubmit={handleSubmit((params) => {
        params.month = `${+params.month + 1}`;

        onSubmit(params);
      })}
    >
      <Grid container spacing={1}>
        <Grid item md={3} sm={4}>
          <ControlledSelect
            control={control}
            name="year"
            fullWidth
            label="Year"
            isLoading={getInvoiceYearsRequestState.isLoading}
            disabled={requestState.isLoading}
            options={invoiceYears.map((x) => ({ value: x, label: `${x}` }))}
            size="small"
          />
        </Grid>
        <Grid item md={3} sm={4}>
          <ControlledSelect
            control={control}
            name="month"
            fullWidth
            label="Month"
            options={availableMonthOptions}
            disabled={requestState.isLoading}
            size="small"
          />
        </Grid>
        <ProviderAccessWrapper>
          <Grid item md={3} sm={4}>
            <ControlledSelect
              control={control}
              name="compareToProviderId"
              fullWidth
              isLoading={getProvidersRequestState.isLoading}
              options={providerToOptionDtos(providers, undefined, "Compare To")}
              disabled={requestState.isLoading}
              size="small"
              label="Compare To"
            />
          </Grid>
        </ProviderAccessWrapper>
        <Grid item md={isProviderUser ? 3 : 6}>
          <Box display="flex" justifyContent="end">
            <div>
              <SpinnerButton
                showSpinner={false}
                disabled={!isValid || requestState.isLoading}
                content="View"
                color="primary"
                variant="contained"
                fullWidth
                type="submit"
              />
            </div>
          </Box>
        </Grid>
      </Grid>
    </form>
  );
};

export default MonthlyDeliverableReportFilter;
